import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useMst } from '../../model/Root';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from '../../components/Layout';
import { DigiMagazine, DigiMagazineFile } from '../../types';
import { color } from '../../theme';
import TextInput from '../../components/FormComponents/TextInput';
import Button from '../../components/Button/Button';
import { DigiMagazineDefaults } from '../../utils/defaults';
import AddNewButton from '../../components/Button/AddButton';
import { Grid, IconButton, TextField, Typography, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import EditIcon from '../../static/edit.svg';
import DeleteIcon from '../../static/delete.svg';
import SaveIcon from '../../static/save.svg';
import UploadButton from '../../components/Button/UploadButton';
import ImageUploadButton from '../../components/Button/ImageUploadButton';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import FormFieldText from 'components/FormComponents/FormFieldText';
import FormFieldTextArea from 'components/FormComponents/FormFieldTextArea';
import FormFieldCheckboxList from 'components/FormComponents/FormFieldCheckboxList';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import AlertModal from 'components/AlertModal';

const getValidationSchema = (t: (key: string) => string, values: DigiMagazine, image: File | undefined) => {
  if (!values.published) {
    const draftSchema = Yup.object().shape({
      name: Yup.string().required(t('common_required')),
      mediaTypeId: Yup.number().required(t('common_required')),
    });

    return draftSchema;
  }

  const digiMagazineSchema = Yup.object().shape({
    name: Yup.string().required(t('common_required')),
    image: Yup.string().test(
      'image-test',
      t('common_required'),
      (value) => values.published || (!!value && value?.length > 0) || !!image
    ),
    publisher: Yup.string().required(t('common_required')),
    mediaTypeId: Yup.number().required(t('common_required')),
    targetGroupIds: Yup.array().min(1, t('common_required')),
  });

  return digiMagazineSchema;
};

const DigiMagazineEditScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    targetGroupStore: { targetGroups, getTargetGroups },
    churchStore: { churches, fetchChurches },
    digiMagazineStore: {
      selectedDigiMagazine,
      getSelectedDigiMagazine,
      updateDigiMagazine,
      createDigiMagazine,
      loading,
    },
  } = useMst();
  const { id } = useParams<{ id: string }>();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>();
  const [selectedImage, setSelectedImage] = useState<File>();
  const [currentDigiMagazineFileId, setCurrentDigiMagazineFileId] = useState(0);
  const [currentFiles, setCurrentFiles] = useState<File[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [digiMagazine, setDigiMagazine] = useState<DigiMagazine>(DigiMagazineDefaults);
  const [digiMagazineFiles, setDigiMagazineFiles] = useState<DigiMagazineFile[]>([]);
  const [digiMagazineFileName, setDigiMagazineFileName] = useState('');
  const [digiMagazineFileMagazineNumber, setDigiMagazineFileMagazineNumber] = useState('');
  const [digiMagazineFileDescription, setDigiMagazineFileDescription] = useState('');
  const [digiMagazineFileImage, setDigiMagazineFileImage] = useState<File>();
  const [digiMagazineFileImagePreviewUrl, setDigiMagazineFileImagePreviewUrl] = useState<string | null>();
  const [digiMagazineFileReleaseDate, setDigiMagazineFileReleaseDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [refreshPage, setRefreshPage] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [fileToDelete, setFileToDelete] = useState<DigiMagazineFile | null>(null);
  const messageRef = useRef<HTMLParagraphElement>(null);
  const counterRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const fetchLists = async () => {
      await getTargetGroups();
    };

    fetchLists();
  }, [getTargetGroups]);

  useEffect(() => {
    if (churches.length === 0) fetchChurches('', true);
  }, [fetchChurches, churches.length]);

  useEffect(() => {
    if (id !== 'create') {
      getSelectedDigiMagazine(id);
    }
  }, [id, getSelectedDigiMagazine]);

  useEffect(() => {
    if (selectedDigiMagazine) {
      if (selectedDigiMagazine.image) {
        setImagePreviewUrl(selectedDigiMagazine.image);
      } else {
        setImagePreviewUrl(null);
      }
      setDigiMagazine(selectedDigiMagazine);
      const files = selectedDigiMagazine.files.map((s) => ({ ...s, state: '' })) ?? [];
      setDigiMagazineFiles(files);
    }
  }, [selectedDigiMagazine]);

  const handleImageSelection = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (target.files && target.files[0]) {
      const newImageFile = target.files[0];
      const image = URL.createObjectURL(newImageFile);
      setSelectedImage(newImageFile);
      setImagePreviewUrl(image);
    }
  };

  const handleDigiMagazineFileImageSelection = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (target.files && target.files[0]) {
      const newFileForIssueCover = target.files[0];
      const image = URL.createObjectURL(newFileForIssueCover);
      setDigiMagazineFileImage(newFileForIssueCover);
      setDigiMagazineFileImagePreviewUrl(image);
    }
  };

  const handleDigiMagazineFileNameChange = (key: string, value: string) => {
    setDigiMagazineFileName(value);
  };

  const handleFilePick = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setCurrentFiles(fileArray);

      if (e.target.files.length === 1) {
        if (digiMagazineFileName === '') {
          const filenameWithoutExt = e.target.files[0].name.split('.').slice(0, -1).join('.');
          setDigiMagazineFileName(filenameWithoutExt);
        }
      } else {
        for (const file of e.target.files) {
          const filenameWithoutExt = file.name.split('.').slice(0, -1).join('.');
          digiMagazineFiles.push({
            name: filenameWithoutExt,
            magazineNumber: digiMagazineFileMagazineNumber,
            order:
              digiMagazineFiles.length === 0 ? 0 : (digiMagazineFiles[digiMagazineFiles.length - 1]?.order ?? -1) + 1,
            src: file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
            releaseDate: digiMagazineFileReleaseDate,
            description: '',
            published: true,
            state: 'new',
          });
          files.push(file);
        }
      }
    }
  };

  const handleDigiMagazineFileSave = () => {
    if (currentDigiMagazineFileId === 0) return;

    if (currentFiles.length === 1 && currentDigiMagazineFileId === -1) {
      const newIssue: DigiMagazineFile = {
        id: new Date().getTime(),
        name: digiMagazineFileName,
        image: digiMagazineFileImage?.name
          .replace(/ /g, '_')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, ''),
        magazineNumber: digiMagazineFileMagazineNumber,
        description: digiMagazineFileDescription,
        order: digiMagazineFiles.length === 0 ? 0 : (digiMagazineFiles[digiMagazineFiles.length - 1]?.order ?? -1) + 1,
        src: currentFiles[0].name.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
        published: true,
        releaseDate: digiMagazineFileReleaseDate,
        state: 'new',
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (newIssue as any).coverImageFile = digiMagazineFileImage;
      digiMagazineFiles.push(newIssue);
      setFiles([...files, currentFiles[0]].filter((item): item is File => !!item));
    } else {
      const index = digiMagazineFiles.findIndex((chapter) => chapter.id === currentDigiMagazineFileId);
      if (index > -1) {
        let itemChanged = false;
        const originalFileItem = digiMagazineFiles[index];
        const updatedFileItem: DigiMagazineFile = { ...originalFileItem };

        if (updatedFileItem.name !== digiMagazineFileName) {
          updatedFileItem.name = digiMagazineFileName;
          itemChanged = true;
        }
        if (updatedFileItem.magazineNumber !== digiMagazineFileMagazineNumber) {
          updatedFileItem.magazineNumber = digiMagazineFileMagazineNumber;
          itemChanged = true;
        }
        if (updatedFileItem.description !== digiMagazineFileDescription) {
          updatedFileItem.description = digiMagazineFileDescription;
          itemChanged = true;
        }
        if (updatedFileItem.releaseDate !== digiMagazineFileReleaseDate) {
          updatedFileItem.releaseDate = digiMagazineFileReleaseDate;
          itemChanged = true;
        }
        if (!updatedFileItem.published) {
          updatedFileItem.published = true;
          itemChanged = true;
        }

        if (digiMagazineFileImage) {
          const newImageName = digiMagazineFileImage.name.replace(/ /g, '_');
          if (updatedFileItem.image !== newImageName) {
            updatedFileItem.image = newImageName;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (updatedFileItem as any).coverImageFile = digiMagazineFileImage;
            itemChanged = true;
          }
        }

        if (itemChanged && updatedFileItem.id) {
          updatedFileItem.state = 'edited';
        } else if (itemChanged && !updatedFileItem.id) {
          updatedFileItem.state = 'new';
        } else if (updatedFileItem.id && updatedFileItem.state !== 'edited') {
          updatedFileItem.state = 'edited';
          itemChanged = true;
        }

        if (itemChanged || originalFileItem.state !== updatedFileItem.state) {
          const newDigiMagazineFiles = [...digiMagazineFiles];
          newDigiMagazineFiles[index] = updatedFileItem;
          setDigiMagazineFiles(newDigiMagazineFiles);
        }
      }
    }
    setEditingId(null);
    setCurrentFiles([]);
    setDigiMagazineFileName('');
    setDigiMagazineFileMagazineNumber('');
    setDigiMagazineFileDescription('');
    setDigiMagazineFileImage(undefined);
    setDigiMagazineFileReleaseDate(format(new Date(), 'yyyy-MM-dd'));
    setCurrentDigiMagazineFileId(0);
  };

  const handleDigiMagazineFileEdit = (digiMagazineFile: DigiMagazineFile) => {
    if (editingId === digiMagazineFile.id) {
      setEditingId(null);
      return;
    }

    setEditingId(digiMagazineFile.id ?? 0);
    setCurrentDigiMagazineFileId(digiMagazineFile.id ?? 0);

    const index = getDigiMagazineFileIndex(digiMagazineFile);
    if (index > -1) {
      const issueData = digiMagazineFiles[index];
      setDigiMagazineFileName(issueData.name);

      // Handle the image preview
      const coverFile = (issueData as any).coverImageFile;
      if (coverFile instanceof File) {
        setDigiMagazineFileImagePreviewUrl(URL.createObjectURL(coverFile));
      } else {
        setDigiMagazineFileImagePreviewUrl(issueData.image);
      }

      setDigiMagazineFileMagazineNumber(issueData.magazineNumber ?? '');
      setDigiMagazineFileDescription(issueData.description ?? '');
      setDigiMagazineFileReleaseDate(issueData.releaseDate ?? format(new Date(), 'yyyy-MM-dd'));
    }
  };

  const handleDeleteFile = (digiMagazineFile: DigiMagazineFile) => {
    setFileToDelete(digiMagazineFile);
  };

  const confirmDelete = () => {
    if (!fileToDelete) return;
    const index = getDigiMagazineFileIndex(fileToDelete);
    if (index > -1) {
      if ((digiMagazineFiles[index].id ?? 0) > 0) {
        digiMagazineFiles[index].state = 'deleted';
      } else {
        digiMagazineFiles.splice(index, 1);
      }
      setRefreshPage(!refreshPage);
    }
    setFileToDelete(null);
  };

  const getDigiMagazineFileIndex = (digiMagazineFile: DigiMagazineFile): number => {
    if (digiMagazineFile.id) {
      return digiMagazineFiles.findIndex((s) => s.id === digiMagazineFile.id);
    }
    return digiMagazineFiles.findIndex((s) => s.name == digiMagazineFile.name && s.order == digiMagazineFile.order);
  };

  const saveDigiMagazine = async (digiMagazineValues: DigiMagazine) => {
    const hasValidId = digiMagazineValues?.id != null && Number(digiMagazineValues.id) > 0;
    if (id !== 'create' && hasValidId) {
      const updatedMagazine = await updateDigiMagazine(digiMagazineValues, digiMagazineFiles, files, selectedImage);
      if (updatedMagazine) {
        setDigiMagazine(updatedMagazine);
        const newFiles = updatedMagazine.files.map((s) => ({ ...s, state: '' })) ?? [];
        setDigiMagazineFiles(newFiles);
      }
    } else {
      const result = await createDigiMagazine(digiMagazineValues, digiMagazineFiles, files, selectedImage);
      if (result != null && Number(result) > 0) {
        navigate(`/digiMagazines/${result}`);
      }
    }
  };

  const renderEditForm = () => {
    return (
      <Grid
        container
        item
        xs={12}
        spacing={1}
        style={{ border: 'solid 1px grey', borderRadius: 3, padding: 20, margin: '10px 0' }}
      >
        {currentFiles.length <= 1 && (
          <>
            <Grid item xs={12}>
              <TextInput
                label={'digiMagazines.digiMagazineFile_name'}
                value={digiMagazineFileName}
                type={'name'}
                placeholder={'digiMagazines.digiMagazineFile_name'}
                onChange={handleDigiMagazineFileNameChange}
                style={{ width: '80%' }}
                multiline={false}
              />
            </Grid>
            <Grid item xs={12} mt={1}>
              <TextInput
                label={'digiMagazines.digiMagazineFile_description'}
                value={digiMagazineFileDescription}
                type={'description'}
                placeholder={'digiMagazines.digiMagazineFile_description'}
                onChange={(key, value) => setDigiMagazineFileDescription(value)}
                style={{ width: '80%' }}
                multiline={true}
              />
            </Grid>
            <Grid item xs={5} mt={2}>
              <DatePicker
                label={t('digiMagazines.digiMagazineFile_release_date')}
                value={new Date(digiMagazineFileReleaseDate)}
                onChange={(newValue: Date | null) => {
                  if (newValue != null && newValue.getFullYear() > 1900) {
                    setDigiMagazineFileReleaseDate(format(newValue, 'yyyy-MM-dd'));
                  }
                  if (newValue == null) {
                    setDigiMagazineFileReleaseDate(format(new Date(), 'yyyy-MM-dd'));
                  }
                }}
                disableMaskedInput
                inputFormat="d.M.yyyy"
                openTo={'day'}
                views={['year', 'month', 'day']}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                renderInput={(props: any) => <TextField size="small" {...props} />}
              />
            </Grid>
            <Grid item xs={12} mt={1}>
              <Typography fontSize={14} fontWeight={'bold'} textTransform={'uppercase'} mb={1}>
                {t('digiMagazines.digiMagazineFile_image')}
              </Typography>
              <ImageUploadButton
                text={t('digiMagazines.upload_digiMagazineFile_image')}
                name="digiMagazines.upload_digiMagazineFile_image"
                handleImageSelection={handleDigiMagazineFileImageSelection}
                imagePreviewUrl={digiMagazineFileImagePreviewUrl}
              />
            </Grid>
            <Grid item xs={12}>
              <p ref={counterRef}></p>
              <p ref={messageRef}></p>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          {currentDigiMagazineFileId === -1 ? (
            <Grid item>
              <UploadButton
                buttonText="digiMagazines.upload_digiMagazineFile"
                onChange={handleFilePick}
                fileName={currentFiles?.length === 1 ? currentFiles[0].name : currentFiles.length > 0 ? 'Useita' : ''}
                fileType=".pdf"
              />
            </Grid>
          ) : (
            <Typography variant="caption" color="GrayText">
              {t('digiMagazines.digiMagazineFile_edit_limited')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <IconButton onClick={handleDigiMagazineFileSave}>
            <img src={SaveIcon} alt="Save" />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const renderDigiMagazineFileUpload = () => {
    const years = [
      ...new Set(
        digiMagazineFiles
          .filter((c) => c.state != 'deleted' && c.releaseDate)
          .map((file) => new Date(file.releaseDate || '').getFullYear().toString())
      ),
    ].sort((a, b) => Number(a) - Number(b));

    const filteredFiles =
      selectedYear === 'all'
        ? digiMagazineFiles
            .filter((c) => c.state != 'deleted')
            .sort((a, b) => new Date(a.releaseDate || '').getTime() - new Date(b.releaseDate || '').getTime())
        : digiMagazineFiles
            .filter((c) => c.state != 'deleted')
            .filter((file) => file.releaseDate && new Date(file.releaseDate).getFullYear().toString() === selectedYear)
            .sort((a, b) => new Date(a.releaseDate || '').getTime() - new Date(b.releaseDate || '').getTime());

    return (
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '16px' }}
        >
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            {t('digiMagazines.digiMagazineFiles')}
          </Typography>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <FormControl size="small" style={{ minWidth: 120 }}>
              <InputLabel>{t('common.year')}</InputLabel>
              <Select
                value={selectedYear}
                label={t('common.year')}
                onChange={(e) => setSelectedYear(e.target.value as string)}
              >
                <MenuItem value="all">{t('common.show_all')}</MenuItem>
                {years.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </Grid>

        {filteredFiles.length > 0 && (
          <Grid item xs={12}>
            <Grid container>
              <Grid container item xs={10} alignSelf={'center'}>
                <Grid item xs={3} alignSelf={'center'}>
                  <Typography variant="body1" fontWeight={'bold'}>
                    {t('digiMagazines.release_date')}
                  </Typography>
                </Grid>
                <Grid item xs={9} alignSelf={'center'}>
                  <Typography variant="body1" fontWeight={'bold'}>
                    {t('common.name')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item xs={2}></Grid>
            </Grid>

            {filteredFiles.map((digiMagazineFile: DigiMagazineFile) => (
              <React.Fragment key={`${digiMagazineFile.id}_${digiMagazineFile.name}`}>
                <Grid container style={{ borderBottom: '1px solid lightGray' }}>
                  <Grid container item xs={10} alignSelf={'center'}>
                    <Grid item xs={3} alignSelf={'center'}>
                      <Typography variant="body1">
                        {digiMagazineFile.releaseDate
                          ? format(new Date(digiMagazineFile.releaseDate), 'dd.MM.yyyy')
                          : ''}
                      </Typography>
                    </Grid>
                    <Grid item xs={9} alignSelf={'center'}>
                      <Typography variant="body1">{digiMagazineFile.name}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container item xs={2} justifyContent={'right'}>
                    <IconButton onClick={() => handleDigiMagazineFileEdit(digiMagazineFile)}>
                      <img src={EditIcon} alt="Edit" />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteFile(digiMagazineFile)}>
                      <img src={DeleteIcon} alt="Delete" />
                    </IconButton>
                  </Grid>
                </Grid>
                {editingId === digiMagazineFile.id && renderEditForm()}
              </React.Fragment>
            ))}
          </Grid>
        )}

        {editingId === 'new' && (
          <Grid item xs={12}>
            {renderEditForm()}
          </Grid>
        )}

        {filteredFiles.length === 0 && editingId !== 'new' && (
          <Grid item xs={9}>
            <Typography variant="body2" color="GrayText">
              {t('digiMagazines.no_digiMagazineFiles_for_year')}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <AddNewButton
            text={t('digiMagazines.add_digiMagazineFile')}
            onClick={() => {
              if (editingId === 'new') {
                setEditingId(null);
              } else {
                setEditingId('new');
                setCurrentDigiMagazineFileId(-1);
                setDigiMagazineFileName('');
                setDigiMagazineFileMagazineNumber('');
                setDigiMagazineFileDescription('');
                setDigiMagazineFileImage(undefined);
                setDigiMagazineFileImagePreviewUrl(undefined);
                setCurrentFiles([]);
                setDigiMagazineFileReleaseDate(
                  format(
                    new Date(Number(selectedYear === 'all' ? new Date().getFullYear() : selectedYear), 0, 1),
                    'yyyy-MM-dd'
                  )
                );
              }
            }}
          />
        </Grid>

        <AlertModal
          open={!!fileToDelete}
          onClose={() => setFileToDelete(null)}
          leftButtonPress={() => setFileToDelete(null)}
          rightButtonPress={confirmDelete}
          title="digiMagazines.confirm_delete_title"
          message={
            fileToDelete
              ? `${t('this_action_cannot_be_reversed')} (${fileToDelete.name})`
              : t('this_action_cannot_be_reversed')
          }
        />
      </Grid>
    );
  };

  const renderHeader = () => {
    const modeText = id !== 'create' ? t('digiMagazines.edit') : t('digiMagazines.add');
    return loading ? t('digiMagazines.loading') : modeText;
  };

  if (loading) {
    return (
      <Layout title={renderHeader()}>
        <CircularProgress size={40} style={{ alignSelf: 'center', marginTop: 30, color: color.primaryButton }} />
        <Typography variant="h5">{t('digiMagazines.loading_text')}</Typography>
      </Layout>
    );
  }

  return (
    <Formik
      initialValues={digiMagazine}
      enableReinitialize
      onSubmit={(values: DigiMagazine) => {
        saveDigiMagazine(values);
      }}
      validationSchema={Yup.lazy((values: DigiMagazine) => getValidationSchema(t, values, selectedImage))}
    >
      {({ isValid, submitForm, setFieldValue, values }: FormikProps<DigiMagazine>) => {
        return (
          <Layout title={renderHeader()}>
            <Grid container spacing={4}>
              <Grid item xs={10}>
                <Typography
                  variant="h4"
                  color={digiMagazine?.published ? 'primary' : 'secondary'}
                  borderColor={digiMagazine?.published ? 'primary' : 'secondary'}
                  border={1}
                  borderRadius={2}
                  sx={{ padding: 1, fontSize: 18, width: 200, marginBottom: 2 }}
                >
                  {digiMagazine?.published ? t('published') : t('draft')}
                </Typography>
              </Grid>

              {/* Add/modify name, description, number and release date */}
              <Grid item xs={10}>
                <FormFieldText label={t('digiMagazines.name')} name="name" />
              </Grid>
              <Grid container item xs={10} spacing={3}>
                <Grid item xs={7}>
                  <FormFieldText label={t('digiMagazines.publisher')} name="publisher" />
                </Grid>
              </Grid>
              <Grid container item xs={10} spacing={3}>
                <Grid item xs={12}>
                  <FormFieldTextArea label={t('digiMagazines.description')} name="description" rows={7} />
                </Grid>
              </Grid>

              {/* Upload cover image */}
              <Grid item xs={10}>
                <ImageUploadButton
                  text="digiMagazines.cover_image"
                  imagePreviewUrl={imagePreviewUrl}
                  handleImageSelection={handleImageSelection}
                />
              </Grid>

              {/* Upload digiMagazine digiMagazineFiles (pdf) */}
              <Grid container item xs={10} spacing={3} ml={3} mt={2}>
                {renderDigiMagazineFileUpload()}
              </Grid>
              {/* Select categories, user groups */}
              <Grid container item xs={12} spacing={3} mt={2}>
                <Grid container item xs={6} spacing={3} alignContent={'flex-start'}>
                  <Grid item xs={12}>
                    <FormFieldCheckboxList label={t('target_groups')} name="targetGroupIds" listItems={targetGroups} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="church-select">{t('church.title') ?? 'Church'}</InputLabel>
                      <Select
                        labelId="church-select"
                        label={t('church.title') ?? 'Church'}
                        value={(values as unknown as { churchId?: string | null }).churchId ?? 'null'}
                        onChange={(e) => setFieldValue('churchId', e.target.value === 'null' ? null : e.target.value)}
                      >
                        <MenuItem value={'null'}>Ei seurakuntaa</MenuItem>
                        {churches.map((c) => (
                          <MenuItem key={c.id} value={String(c.id ?? '')}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* publish or save as draft */}
                  <Grid container item xs={10} spacing={3}>
                    <Button
                      text="save_draft"
                      onClick={() => {
                        setFieldValue('published', false);
                        submitForm();
                      }}
                      backgroundColor={color.secondaryButton}
                      width={220}
                      marginBottom={30}
                    />
                    <Button
                      text="publish"
                      onClick={() => {
                        setFieldValue('published', true);
                        submitForm();
                      }}
                      marginBottom={30}
                      disabled={!isValid}
                    />
                  </Grid>
                </Grid>
                <Grid container item xs={6} spacing={3}></Grid>
              </Grid>
            </Grid>
          </Layout>
        );
      }}
    </Formik>
  );
});

export default DigiMagazineEditScreen;
